import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { ProposalLine } from "@/types/dolibarr";

export function formatCurrency(amount: number | string | null | undefined): string {
  const num = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0);
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(isNaN(num) ? 0 : num);
}

export function formatDate(timestamp: string | number | null | undefined): string {
  if (!timestamp) return "-";
  try {
    const ts = typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
    if (isNaN(ts) || ts === 0) return "-";
    const date = new Date(ts * 1000);
    return format(date, "dd/MM/yyyy", { locale: fr });
  } catch {
    return "-";
  }
}

export function formatDateLong(timestamp: string | number | null | undefined): string {
  if (!timestamp) return "-";
  try {
    const ts = typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
    if (isNaN(ts) || ts === 0) return "-";
    const date = new Date(ts * 1000);
    return format(date, "dd MMMM yyyy", { locale: fr });
  } catch {
    return "-";
  }
}

// Dolibarr returns array_options as [] (empty JSON array) instead of {}
// when no extrafields are set (PHP associative array JSON serialization quirk).
// This helper normalizes and safely reads extrafields.
export function normalizeArrayOptions<T = Record<string, unknown>>(
  arrayOptions: unknown
): T {
  if (arrayOptions && typeof arrayOptions === "object" && !Array.isArray(arrayOptions)) {
    return arrayOptions as T;
  }
  return {} as T;
}

export function getExtrafield<T = string>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: { array_options?: any } | null | undefined,
  key: string
): T | null {
  const opts = normalizeArrayOptions(obj?.array_options);
  const fullKey = key.startsWith("options_") ? key : `options_${key}`;
  const val = (opts as Record<string, unknown>)[fullKey];
  if (val === undefined || val === null || val === "") return null;
  return val as T;
}

/**
 * Référence affichable pour une ligne de devis (API Dolibarr fragmentée).
 * L'API omet souvent `ref` sur la ligne alors que `product_ref`, `label`,
 * un extrafield ou un `fk_product` renseigné existent côté Dolibarr.
 */
export function getProposalLineDisplayRef(line: Partial<ProposalLine>): string {
  const str = (v: unknown) =>
    v != null && String(v).trim() !== "" ? String(v).trim() : "";

  const pr = str(line.product_ref);
  if (pr) return pr;

  // Dolibarr joint parfois le code-barres produit sur la ligne
  const pbc = str(line.product_barcode);
  if (pbc) return pbc;

  const lb = str(line.label);
  if (lb) return lb;

  const rf = str(line.ref);
  if (rf) return rf;

  const refFourn = getExtrafield<string>(line, "ref_fournisseur");
  if (refFourn) return refFourn.trim();

  // Autres extrafields « ref » courants (noms variables selon instance)
  const opts = normalizeArrayOptions(line.array_options);
  for (const [key, val] of Object.entries(opts)) {
    if (typeof val !== "string" || !val.trim()) continue;
    const k = key.toLowerCase();
    if (
      k === "options_ref_fournisseur" ||
      k.endsWith("_ref_produit") ||
      k.endsWith("_ref_article") ||
      k === "options_ref" ||
      k === "options_reference"
    ) {
      return val.trim();
    }
  }

  const fk = line.fk_product != null ? String(line.fk_product).trim() : "";
  if (fk && fk !== "0") return fk;
  return "";
}

export type EtatDevisKey =
  | "en_cours"
  | "envoye"
  | "accepte"
  | "refuse"
  | "abandonne"
  | "en_attente";

export interface EtatDevisConfig {
  label: string;
  badgeClass: string;
  bgClass: string;
  dotClass: string;
}

export const ETAT_DEVIS_CONFIG: Record<EtatDevisKey, EtatDevisConfig> = {
  en_cours: {
    label: "En cours",
    badgeClass: "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300",
    bgClass: "bg-blue-50 dark:bg-blue-950/30",
    dotClass: "bg-blue-500",
  },
  envoye: {
    label: "Envoyé",
    badgeClass: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/60 dark:text-yellow-300",
    bgClass: "bg-yellow-50 dark:bg-yellow-950/30",
    dotClass: "bg-yellow-500",
  },
  accepte: {
    label: "Accepté",
    badgeClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300",
    bgClass: "bg-emerald-50 dark:bg-emerald-950/30",
    dotClass: "bg-emerald-500",
  },
  refuse: {
    label: "Refusé",
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300",
    bgClass: "bg-red-50 dark:bg-red-950/30",
    dotClass: "bg-red-500",
  },
  abandonne: {
    label: "Abandonné",
    badgeClass: "bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400",
    bgClass: "bg-gray-50 dark:bg-gray-950/30",
    dotClass: "bg-gray-400",
  },
  en_attente: {
    label: "En attente",
    badgeClass: "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300",
    bgClass: "bg-slate-50 dark:bg-slate-950/30",
    dotClass: "bg-slate-400",
  },
};

export const ETAT_DEVIS_OPTIONS = (Object.keys(ETAT_DEVIS_CONFIG) as EtatDevisKey[]).map(
  (key) => ({
    value: key,
    label: ETAT_DEVIS_CONFIG[key].label,
  })
);

/** Anciennes valeurs extrafield retirées de l’UI : affichage unifié. */
const LEGACY_ETAT_DISPLAY: Record<string, EtatDevisKey> = {
  signe: "accepte",
  negocie: "envoye",
};

export function getEtatDevisConfig(etat: string | null | undefined): EtatDevisConfig {
  if (!etat) return ETAT_DEVIS_CONFIG.en_cours;
  const mapped = LEGACY_ETAT_DISPLAY[etat] ?? etat;
  return ETAT_DEVIS_CONFIG[mapped as EtatDevisKey] ?? ETAT_DEVIS_CONFIG.en_cours;
}

// Native Dolibarr proposal statuses (0-4) mapped to our EtatDevisKey
// Used as fallback when options_etat_devis is not set
const NATIVE_STATUS_MAP: Record<string, EtatDevisKey> = {
  "0": "en_cours", // Brouillon
  "1": "en_cours", // Validé
  "2": "accepte", // Signé / accepté côté Dolibarr
  "3": "refuse", // Refusé
  "4": "accepte", // Facturé → assimilé à accepté
};

/**
 * Resolve the display status for a proposal.
 * Priority: options_etat_devis extrafield > native Dolibarr statut fallback
 */
/** Convert Dolibarr timestamp (seconds) or date string to YYYY-MM-DD for <input type="date"> */
export function timestampToDateStr(val: string | number | null | undefined): string {
  if (val == null || val === "" || val === "0") return "";
  const num = Number(val);
  if (!isNaN(num) && num > 946684800) {
    return new Date(num * 1000).toISOString().split("T")[0];
  }
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    return val.split("T")[0];
  }
  return "";
}

/** Convert YYYY-MM-DD to Dolibarr timestamp (seconds since epoch) */
export function dateStrToTimestamp(dateStr: string): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00Z");
  return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 1000);
}

/**
 * Aplatit les extrafields pour un PUT Dolibarr (array_options en chaînes / nombres stables).
 * Évite les décimales excessives et les valeurs non sérialisables qui peuvent faire échouer le serveur.
 */
export function flattenArrayOptionsForDolibarrApi(
  input: Record<string, unknown>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "object" && !Array.isArray(v)) continue;
    if (Array.isArray(v)) continue;
    if (typeof v === "number" && Number.isFinite(v)) {
      const isTimestampKey =
        k.includes("date") || k.includes("_ts") || k.endsWith("_time");
      if (isTimestampKey) {
        out[k] = String(Math.floor(v));
      } else {
        const rounded = Math.round(v * 100) / 100;
        out[k] = String(rounded);
      }
    } else if (typeof v === "boolean") {
      out[k] = v ? "1" : "0";
    } else {
      out[k] = String(v).trim();
    }
  }
  return out;
}

export function resolveProposalEtat(
  proposal: { array_options?: unknown; statut?: number | string; status?: number | string } | null | undefined
): EtatDevisKey {
  const etatExtra = getExtrafield<string>(proposal, "etat_devis");
  if (etatExtra) {
    const normalized = LEGACY_ETAT_DISPLAY[etatExtra];
    if (normalized) return normalized;
    if (etatExtra in ETAT_DEVIS_CONFIG) return etatExtra as EtatDevisKey;
  }
  const nativeStatus = String(proposal?.statut ?? proposal?.status ?? "0");
  return NATIVE_STATUS_MAP[nativeStatus] || "en_cours";
}
